/**
 * Typed artefact-kind API helpers — Track 2.G.
 *
 * Thin wrappers around the generic `ApiClient` that narrow input/output types
 * to artefact-kind-specific shapes.
 *
 * Architecture refs:
 *   functionality.md §3 — artefact-kind domain model
 *   cook-as-process-design §2.2 — seed kinds + user-defined kinds
 *   cook-as-process-design §2.5 — kind-level traits
 *   architecture.md §4.7 — DDL: artefact_kinds
 *   architecture.md §4.23 — server-stamped author_agent; NOT accepted from client
 *
 * Security invariant (CLAUDE.md §3 + architecture.md §4.23):
 *   None of these helpers accept an `authorAgent` parameter.
 */

import type { ApiClient } from "./index.js";

// ── Row type ──────────────────────────────────────────────────────────────────

export interface ArtefactKindRow {
  uuid: string;
  slug: string;
  name: string;
  description: string | null;
  seed_kind_uuid: string | null;
  /** JSON array, e.g. '["fed","observable"]'. */
  traits_json: string;
  /** JSON object of typed per-kind fields, or null. */
  fields_json: string | null;
  /** 1 = built-in seed kind, 0 = user-defined. */
  is_builtin: number;
  author_uuid: string;
  created_at: number;
  updated_at: number;
}

// ── Input types ───────────────────────────────────────────────────────────────

export interface ArtefactKindCreateInput {
  slug: string;
  name: string;
  description?: string | null;
  seedKindUuid?: string | null;
  traitsJson?: string;
  fieldsJson?: string | null;
}

export interface ArtefactKindUpdateInput {
  uuid: string;
  name?: string;
  slug?: string;
  description?: string | null;
  traitsJson?: string;
  fieldsJson?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * List all artefact kinds (seed + user-defined).
 * Seed kinds are returned first (is_builtin = 1), then user-defined by created_at.
 */
export async function listArtefactKinds(api: ApiClient): Promise<{ kinds: ArtefactKindRow[] }> {
  return (await api.artefactKind.list()) as { kinds: ArtefactKindRow[] };
}

/**
 * Get a single artefact kind by UUID.
 *
 * @throws `ORPCError("NOT_FOUND")` if not found.
 */
export async function getArtefactKind(api: ApiClient, uuid: string): Promise<ArtefactKindRow> {
  return (await api.artefactKind.get({ uuid })) as ArtefactKindRow;
}

/**
 * Create a user-defined artefact kind.
 *
 * `seedKindUuid` — copy behaviour from an existing kind (e.g. the `culture`
 *   seed kind UUID, making `wheel` inherit `culture`'s fed + observable traits
 *   as a starting point before `traitsJson` override).
 * `fieldsJson` — JSON object of typed per-kind field declarations per
 *   extensibility-design §3.9. Example: `{"cave_humidity":{"type":"percentage"},"age_days":{"type":"duration","unit":"d"}}`.
 *
 * @throws `ORPCError("CONFLICT")` if slug is taken.
 * @throws `ORPCError("BAD_REQUEST")` if slug is malformed.
 * @throws `ORPCError("NOT_FOUND")` if seedKindUuid does not exist.
 */
export async function createArtefactKind(
  api: ApiClient,
  input: ArtefactKindCreateInput,
): Promise<ArtefactKindRow> {
  return (await api.artefactKind.create(input)) as ArtefactKindRow;
}

/**
 * Update a user-defined artefact kind.
 *
 * @throws `ORPCError("FORBIDDEN")` for built-in seed kinds.
 * @throws `ORPCError("CONFLICT")` if slug rename conflicts.
 */
export async function updateArtefactKind(
  api: ApiClient,
  input: ArtefactKindUpdateInput,
): Promise<ArtefactKindRow> {
  return (await api.artefactKind.update(input)) as ArtefactKindRow;
}

/**
 * Delete a user-defined artefact kind.
 *
 * @throws `ORPCError("FORBIDDEN")` for built-in seed kinds.
 * @throws `ORPCError("CONFLICT")` if artefact instances still reference this kind.
 */
export async function deleteArtefactKind(api: ApiClient, uuid: string): Promise<{ ok: boolean }> {
  return (await api.artefactKind.delete({ uuid })) as { ok: boolean };
}
